<template>
  <div class="relative flex flex-col justify-center w-full">
    <form
      class="w-full space-y-6"
      @submit.prevent="next"
      @keydown.right.prevent="next"
      @keydown.enter.prevent="next"
    >
      <div
        @click="showTooltip = !showTooltip"
        class="flex flex-row space-x-2 text-2xl font-bold text-bright-gray dark:text-white lg:text-4xl"
      >
        <h2>{{ item.questionDisplay }}</h2>
        <span v-if="item.required" class="text-ruby">*</span>
        <span class="cursor-pointer" v-if="item.tooltip.length"
          ><InformationCircleIcon class="w-8 h-8 text-gold"
        /></span>
      </div>
      <label for="loan_amount" class="text-sm text-navada"
        >{{ item.placeholder }}
      </label>
      <input
        v-focus
        id="loan_amount"
        name="loan_amount"
        :value="useFormatterWithOptions({ value: selected })"
        @change="updateValue"
        class="flex rounded-lg w-full h-[60px] text-xl border-gold dark:text-white dark:bg-[#1b1c20] dark:border-gold focus:ring-0 border-2 focus focus:border-gold"
        type="text"
      />
      <!-- Desktop action -->
      <div
        class="flex-col justify-between hidden w-full pt-4 md:flex md:w-full md:flex-row"
      >
        <div class="flex flex-row space-x-4">
          <button
            type="button"
            @click="next"
            class="inline-flex items-center justify-center flex-shrink-0 min-w-0 px-8 py-2 text-sm font-bold text-center text-black rounded-full bg-gold-alt focus:outline-none"
          >
            <span v-if="!isLoading">{{ t("input.continue") }}</span>
            <span v-else class="flex flex-row space-x-4"
              ><span>{{ t("input.saving") }}</span>
              <Spinner text-color="text-black"
            /></span>
          </button>
          <button
            type="submit"
            class="inline-flex items-center justify-center px-8 py-2 space-x-1 text-sm font-bold text-white rounded-full focus:outline-none"
          >
            <span class="text-navada">press</span>
            <span class="text-black dark:text-white">{{
              t("input.enter")
            }}</span>
            <span>
              <ReturnIcon />
            </span>
          </button>
        </div>
        <button
          v-if="!item.required"
          @click="$emit('skip', item, selected)"
          class="inline-flex items-center justify-center px-8 py-2 space-x-1 text-sm font-bold text-white rounded-full focus:outline-none"
        >
          <span class="text-black dark:text-white">{{ t("input.skip") }}</span>
          <SkipIcon />
        </button>
      </div>
      <!-- Mobile action -->
      <div class="flex flex-col w-full pt-4 space-y-4 md:hidden">
        <div class="flex flex-row w-full">
          <button
            type="button"
            @click="next"
            class="inline-flex items-center justify-center flex-shrink-0 w-full min-w-0 px-8 py-2 text-sm font-bold text-center text-black rounded-full bg-gold-alt focus:outline-none"
          >
            <span v-if="!isLoading">{{ t("input.continue") }}</span>
            <span v-else class="flex flex-row space-x-4"
              ><span>{{ t("input.saving") }}</span>
              <Spinner text-color="text-black"
            /></span>
          </button>
        </div>
        <div class="flex flex-row justify-end">
          <button
            v-if="!item.required"
            @click="$emit('skip', item, selected)"
            class="inline-flex items-center justify-center py-2 space-x-1 text-sm font-bold text-white rounded-full focus:outline-none"
          >
            <span class="text-black dark:text-white">{{
              t("input.skip")
            }}</span>
            <SkipIcon />
          </button>
        </div>
      </div>
    </form>

    <!-- Tooltip -->
    <tooltip-modal
      :show="showTooltip && item.tooltip.length"
      :tooltip="item.tooltip"
      @close="showTooltip = !showTooltip"
    />
    <!-- End tooltip -->

    <!-- Error -->
    <error-modal
      :show="showError"
      :error="error"
      @close="
        showError = !showError;
        error = '';
      "
    />
    <!-- End Error -->
  </div>
</template>

<script>
import { InformationCircleIcon } from "@heroicons/vue/solid";
import { computed, reactive, toRefs } from "vue";
import { useI18n } from "vue-i18n";
import Spinner from "../Spinner";
import ReturnIcon from "../icons/ReturnIcon";
import SkipIcon from "../icons/SkipIcon";
import { useFormatterWithOptions } from "../../util";

export default {
  name: "Number",
  props: {
    isLoading: {
      type: Boolean,
      default: false,
    },
    item: {
      type: Object,
      required: true,
    },
  },
  components: {
    InformationCircleIcon,
    SkipIcon,
    ReturnIcon,
    Spinner,
  },
  setup(props, { emit }) {
    const { t } = useI18n();

    const answer = computed(() => props.item.answer);

    const state = reactive({
      showTooltip: false,
      showError: false,
      error: "",
      selected: answer.value ? answer.value.answer : "",
    });

    const updateValue = (e) => {
      const val = e.target.value.toString().replace(/[^0-9]/g, "");
      if (val > 250000) {
        state.selected = 250000;
      } else if (val < 0) {
        state.selected = 1000;
      } else {
        state.selected = val;
      }
    };

    const next = () => {
      if (
        props.item.required &&
        (state.selected === null ||
          state.selected === "" ||
          state.selected.length === 0)
      ) {
        state.showError = true;
        state.error = t("input.requiredAmount");
        return;
      }

      if (
        parseInt(state.selected) < 10000 ||
        parseInt(state.selected) > 250000
      ) {
        state.showError = true;
        state.error = t("input.requiredAmount");
        return;
      }
      emit("next", props.item, state.selected);
    };

    return {
      t,
      useFormatterWithOptions,
      next,
      updateValue,
      ...toRefs(state),
    };
  },
};
</script>
